import React, {useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {setTrajectoryStore} from '../store/entities/trajectory';
import {toast} from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import '../App.css';
import {
  Button,
  Checkbox,
  Divider,
  Input,
  InputNumber,
  Modal,
  Popover,
  Select,
  Skeleton,
  Slider,
  Space,
  Switch,
  Tooltip
} from 'antd';
import InputMap from "./InputMap";
import {useParams} from "react-router-dom";
import {findEquidistantPointsAlongRoute} from "../util/gis";
import Logos from "./Logos";
import * as wellknown from 'wellknown'
import {useDebouncedEffect} from "./util/UseDebouncedEffect";
import $ from 'jquery';
import Fuse from "fuse.js";
import {ExclamationCircleOutlined, QuestionCircleOutlined} from '@ant-design/icons';
import {useAppSelector} from "../store/hooks";
import {DASHBOARDS_BACKEND_URL} from "./util/config";
import DateRangeSelector from "./DateRange/DateRangeSelector";

const {Option, OptGroup} = Select;

const useDarkMode = localStorage.getItem('darkMode') === 'true'

const InputPage = props => {
  const inputMapRef = useRef();

  const dispatch = useDispatch();
  // Get the URL parameters
  let params = useParams();

  // Grab stuff from the store
  const store = {
    startDate: useAppSelector(state => state.entities.trajectory.startDate),
    endDate: useAppSelector(state => state.entities.trajectory.endDate),
    dateRangeId: useAppSelector(state => state.entities.trajectory.dateRangeId),
    routeId: useAppSelector(state => state.entities.trajectory.routeId),
    routeName: useAppSelector(state => state.entities.trajectory.routeName),
    roadWidth: useAppSelector(state => state.entities.trajectory.roadWidth),
    mileMarkerOffset: useAppSelector(state => state.entities.trajectory.mileMarkerOffset),
    subtractMileMarkers: useAppSelector(state => state.entities.trajectory.subtractMileMarkers),
    flipMileMarkers: useAppSelector(state => state.entities.trajectory.flipMileMarkers),
    includeTrucks: useAppSelector(state => state.entities.trajectory.includeTrucks),
    waypoints: useAppSelector(state => state.entities.trajectory.waypoints),
    route: useAppSelector(state => state.entities.trajectory.route),
    distanceBetweenPoints: useAppSelector(state => state.entities.trajectory.distanceBetweenPoints),
    role: useAppSelector(state => state.entities.trajectory.role),
    loadedNewHeatmap: useAppSelector(state => state.entities.trajectory.loadedNewHeatmap),
    sessionId: useAppSelector(state => state.entities.trajectory.sessionId),
    organizationName: useAppSelector(state => state.entities.trajectory.organizationName),
    maxTravelTime: useAppSelector(state => state.entities.trajectory.maxTravelTime),
    // loadedMaxTravelTime: useAppSelector(state => state.entities.trajectory.loadedMaxTravelTime),
    heatmapId: useAppSelector(state => state.entities.trajectory.heatmapId),
    showOnlyMyRoutes: useAppSelector(state => state.entities.trajectory.showOnlyMyRoutes),
    userEmail: useAppSelector(state => state.entities.trajectory.userEmail),
    routeCreatorEmail: useAppSelector(state => state.entities.trajectory.routeCreatorEmail),
    travelTimes: useAppSelector(state => state.entities.trajectory.travelTimes),
    movementTable: useAppSelector(state => state.entities.trajectory.movementTable),
    truckTable: useAppSelector(state => state.entities.trajectory.truckTable),
    accelThreshold: useAppSelector(state => state.entities.trajectory.accelThreshold)
  }
  // Setup the local variables. Use values from the store
  const [roadWidth, setRoadWidth] = useState(store.roadWidth)
  const [mileMarkerOffset, setMileMarkerOffset] = useState(store.mileMarkerOffset)
  const [subtractMileMarkers, setSubtractMileMarkers] = useState(store.subtractMileMarkers)
  const [flipMileMarkers, setFlipMileMarkers] = useState(store.flipMileMarkers)
  const [includeTrucks, setIncludeTrucks] = useState(store.includeTrucks);
  const [routeName, setRouteName] = useState(null);
  const [waypoints, setWaypoints] = useState(store.waypoints);
  const [subtractMileMarkersDisabled, setSubtractMileMarkersDisabled] = useState(true);
  const [dateRangeModalVisible, setDateRangeModalVisible] = useState(false);
  const [routeModalVisible, setRouteModalVisible] = useState(false);
  const [routes, setRoutes] = useState(null);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [routeSelectDisabled, setRouteSelectDisabled] = useState(true);
  const [editRouteDisabled, setEditRouteDisabled] = useState(true);
  const [maxTravelTime, setMaxTravelTime] = useState(0);
  const [suggestedMileMarker, setSuggestedMileMarker] = useState(null);
  const [accelThreshold, setAccelThreshold] = useState(store.accelThreshold)

  useEffect(() => {
    if (!routes && !isLoadingRoutes) getRoutes();
  }, [])

  useEffect(() => syncWithStore())

  useEffect(() => {
    if (store.loadedNewHeatmap) {
      syncWithStore()
      dispatch(setTrajectoryStore({loadedNewHeatmap: false}));
    }
  }, [store.loadedNewHeatmap])

  useEffect(() => {
    if (!isLoadingRoutes) getRoutes();
  }, [store.showOnlyMyRoutes, store.userEmail, store.organizationName])

  useEffect(() => {
    // Make sure that routes exist, and that the selected route is in the list of routes available to us (excludes routes in other orgs)
    // Also, make sure that our component state and the store are sync-ed up.
    if (!!routes && !routes.find(r => r.route_id === store.routeId)) setEditRouteDisabled(true);
    else setEditRouteDisabled(false);
  }, [store.routeId, routes])

  useDebouncedEffect(() => {
    setSuggestedMileMarker(null);
    if (routeModalVisible && waypoints) getClosestRefpoint();
  }, [waypoints], 500)

  const syncWithStore = () => {
    setRoadWidth(store.roadWidth);
    setFlipMileMarkers(store.flipMileMarkers);
    setSubtractMileMarkers(store.subtractMileMarkers);
    setIncludeTrucks(store.includeTrucks);
    setWaypoints(store.waypoints);
    setAccelThreshold(store.accelThreshold);
  }

  const getClosestRefpoint = () => {
    let waypointsJson = wellknown.parse(waypoints)
    let startPoint = waypointsJson.coordinates[0]
    let startPointWkt = wellknown.stringify({type: 'Point', coordinates: [startPoint]}).replace(',', ' ')
    // TODO Un-hard-code this
    if (store.sessionId && ['Default', 'JTRP', 'INDOT', 'Testing','TestingJCD','JTRP-New','INDOT-New','Caltrans-New','CTDOT-New','GDOT-New','MnDOT-New','MSDOT-New','NCDOT-New','ODOT-New','PennDOT-New','TxDOT-New','UDOT-New','WisDOT-New'].includes(store.organizationName)) {
      $.ajax(`${DASHBOARDS_BACKEND_URL}trajectory_heatmap_route/closest_refpoint/${startPointWkt}`, {
        method: 'GET',
        beforeSend: xhr => {
          xhr.setRequestHeader('Authorization', `Bearer ${store.sessionId}`);
        },
        success: data => {
          if ('mm' in data) setSuggestedMileMarker(data.mm);
          else setSuggestedMileMarker(null);
        },
        error: err => setSuggestedMileMarker(null)
      })
    }
  }

  const getRoutes = (createdRouteId = null, createDuplicate = false) => {
    if (store.sessionId && store.organizationName) {
      $.ajax(`${DASHBOARDS_BACKEND_URL}trajectory_heatmap_route/${store.organizationName}`, {
        method: 'GET',
        beforeSend: xhr => {
          setIsLoadingRoutes(true);
          xhr.setRequestHeader('Authorization', `Bearer ${store.sessionId}`);
        },
        success: data => {
          let newRoutes;
          if (store.userEmail && store.showOnlyMyRoutes) {
            newRoutes = data.filter(r => r.user_email === store.userEmail);
          } else newRoutes = data;
          setRoutes(newRoutes);
          if (createdRouteId) {
            // dispatch(setTrajectoryStore({routeId: createdRouteId}))
            routeIdChange(createdRouteId, newRoutes);
          }
          if (!createDuplicate) setRouteModalVisible(false);
          setConfirmLoading(false);
          setRouteSelectDisabled(false);
          setIsLoadingRoutes(false);
        },
        error: err => {
          showErrorToast("Error getting routes!");
          if (!createDuplicate) {
            setRouteModalVisible(false);
          }
          setConfirmLoading(false);
          setIsLoadingRoutes(false);
        }
      })

    }
  }

  const deleteRoute = () => {
    if (store.sessionId) {
      $.ajax(`${DASHBOARDS_BACKEND_URL}trajectory_heatmap_route/${store.routeId}`, {
        method: 'DELETE',
        beforeSend: xhr => {
          xhr.setRequestHeader('Authorization', `Bearer ${store.sessionId}`);
          setDeleteLoading(true);
        },
        success: data => {
          setDeleteLoading(false)
          setRouteModalVisible(false)
          toast.success(data);
          routeIdChange(null)
          getRoutes(null);
        },
        error: err => {
          // That route already exists
          console.log(err);
          toast.error("Error deleting route!");
          setRouteModalVisible(false);
          setDeleteLoading(false)
        },
      })
    }

  }

  const saveRoute = (createDuplicate = false) => {
    let reqRouteId = createDuplicate ? null : store.routeId
    let data = {
      'route_name': createDuplicate ? (routeName ? `${routeName} (copy)` : '(copy)') : routeName,
      'road_width': roadWidth,
      'mile_marker_offset': mileMarkerOffset,
      'flip_mile_markers': flipMileMarkers,
      'include_trucks': includeTrucks,
      'subtract_mile_markers': subtractMileMarkers,
      'route_waypoints': waypoints,
      // ...(store.loadedMax && {startDate: startDateParams}),
      'max_travel_time': maxTravelTime
    };
    if (store.sessionId) {
      $.ajax(`${DASHBOARDS_BACKEND_URL}trajectory_heatmap_route/${reqRouteId ? reqRouteId : ''}`, {
        method: reqRouteId ? 'PUT' : 'POST',
        data: JSON.stringify(data),
        contentType: 'application/json',
        dataType: 'json',
        beforeSend: xhr => {
          xhr.setRequestHeader('Authorization', `Bearer ${store.sessionId}`);
          setConfirmLoading(true);
        },
        success: data => {
          if (data.length >= 1) {
            getRoutes(data[0].route_id, createDuplicate);
          }
          if (createDuplicate) toast.success('Duplicated route!')
        },
        error: err => {
          // That route already exists
          if (err.status === 409) {
            dispatch(setTrajectoryStore({routeId: err.responseJSON[0].route_id}))
            getRoutes();
          } else {
            console.log(err);
            toast.error("Error creating route!");
            if (!createDuplicate) {
              setRouteModalVisible(false);
              setConfirmLoading(false);
            }
          }
        },
      })
    }
  }

  const createTrajectoryHeatmap = () => {
    if (!store.routeId) {
      showErrorToast("No route selected!");
      return;
    }
    if (!store.dateRangeId) {
      showErrorToast("No date range selected!")
      return;
    }
    if (store.sessionId) {
      let data = {
        'date_range_id': store.dateRangeId,
        'route_id': store.routeId
      };
      if (store.sessionId) {
        $.ajax(`${DASHBOARDS_BACKEND_URL}trajectory_heatmap/`, {
          method: 'POST',
          data: JSON.stringify(data),
          contentType: 'application/json',
          dataType: 'json',
          beforeSend: xhr => {
            xhr.setRequestHeader('Authorization', `Bearer ${store.sessionId}`);
            setSaveLoading(true);
          },
          success: data => {
            if (data.length < 1) return;
            dispatch(setTrajectoryStore({
              heatmapId: data[0].trajectory_heatmap_id,
              // submitClicked: true
            }));
            let url = new URL(window.location);
            let newUrlString = `${url.origin}/${data[0].trajectory_heatmap_id}`;
            window.history.pushState({}, '', newUrlString)
            setSaveLoading(false);
          },
          error: err => {
            if (err.status === 409) {
              if (data.length < 1) return;
              dispatch(setTrajectoryStore({
                heatmapId: err.responseJSON[0].trajectory_heatmap_id,
                // submitClicked: true
              }));
              let url = new URL(window.location);
              let newUrlString = `${url.origin}/${err.responseJSON[0].trajectory_heatmap_id}`;
              window.history.replaceState({}, '', newUrlString)
              setSaveLoading(false);
            } else {
              toast.error("Error creating heatmap!");
              setSaveLoading(false);
            }
          }
        })
      }
    }
  }

  // Update the URL but don't submit!
  useDebouncedEffect(() => {
    dispatch(setTrajectoryStore({maxTravelTime}));
  }, [maxTravelTime], 500)

  /**
   * Show the edit route modal
   * @param updateRoute If false, the route won't be updated before opening. Set to false when creating a new route.
   */
  const showRouteModal = (updateRoute = true) => {
    setRouteModalVisible(true);
    // Make sure the local state is synced up with the store
    if (updateRoute) routeIdChange(store.routeId);
  }
  const routeModalHandleOk = () => {
    // Validate inputs
    if (!waypoints) showErrorToast("Please select points on the map!");
    else {
      if (localStorage.getItem('showRouteSaveConfirm') === "false" || !store.routeId) {
        saveRoute();
      } else {
        confirmSave();
      }
    }
  }

  const routeModalHandleDelete = () => {
    confirmDelete();
  }

  const routeModalHandleCancel = () => {
    setRouteModalVisible(false);
    routeIdChange(store.routeId);
  }

  const showErrorToast = toast.error

  const routeIdChange = (routeId, allRoutes = routes) => {
    let route;
    if (!routeId) {
      route = {
        'route_id': null,
        'route_name': null,
        'route': null,
        'route_waypoints': null,
        'road_width': 100,
        'mile_marker_offset': 0,
        'flip_mile_markers': false,
        'include_trucks': false,
        'subtract_mile_markers': false,
        'max_travel_time': 0,
        'event_table': null,
        'movement_table': null,
        'truck_table': null,
        'user_email': null
      }
    } else if (allRoutes) {
      route = allRoutes.find(r => r.route_id === routeId);
    }
    if (!route) {
      return;
    }
    dispatch(setTrajectoryStore({
      routeId: routeId,
      routeName: route.route_name,
      routeCreatorEmail: route.user_email,
      waypoints: route.route_waypoints,
      roadWidth: route.road_width,
      mileMarkerOffset: route.mile_marker_offset,
      flipMileMarkers: route.flip_mile_markers,
      subtractMileMarkers: route.subtract_mile_markers,
      includeTrucks: route.include_trucks,
      maxTravelTime: route.max_travel_time,
      eventTable: route.event_table,
      movementTable: route.movement_table,
      truckTable: route.truck_table,
      accelThreshold: store.accelThreshold ?? 0.25
    }))
    setRouteName(route.route_name)
    setWaypoints(route.route_waypoints)
    setRoadWidth(route.road_width)
    setMileMarkerOffset(route.mile_marker_offset)
    setFlipMileMarkers(route.flip_mile_markers)
    setSubtractMileMarkers(route.subtract_mile_markers)
    setIncludeTrucks(route.include_trucks)
    setMaxTravelTime(route.max_travel_time)
    setAccelThreshold(store.accelThreshold ?? 0.25)
  }

  // Updates subtractMileMarkersDisabled when the offset or route changes
  useEffect(() => {
    if (store.route) {
      let equidistantRoute = findEquidistantPointsAlongRoute(store.route, store.distanceBetweenPoints);
      let maxMileMarker = Math.max(...equidistantRoute.mileMarkers);
      setSubtractMileMarkersDisabled(mileMarkerOffset < maxMileMarker);
    }
  }, [mileMarkerOffset, store.route]);

  const confirmSave = () => {
    Modal.confirm({
      title: "Confirm",
      icon: <ExclamationCircleOutlined/>,
      content: (
        <div>
          <div>
            Are you sure you want to save? This will modify any heatmaps that are based on this route, <b>and
            cannot be undone</b>.
          </div>
          <br/>
          <Checkbox
            onChange={e => localStorage.setItem('showRouteSaveConfirm', (e.target.value) ? 'true' : 'false')}>Don't
            show this again</Checkbox>
        </div>),
      okText: 'Save',
      cancelText: 'Cancel',
      onOk: () => {
        saveRoute();
      },
      onCancel: () => {
        // setRouteModalVisible(false);
      }
    })
  }

  const confirmDelete = () => {
    Modal.confirm({
      title: "Confirm",
      icon: <ExclamationCircleOutlined/>,
      content: (
        <div>
          Are you sure you want to delete this route? This action will delete all heatmaps that are based
          on this route, <b>and cannot be undone</b>.
        </div>
      ),
      okText: 'Delete',
      okButtonProps: {danger: true},
      cancelText: 'Cancel',
      onOk: () => {
        deleteRoute()
      },
      onCancel: () => {
      }
    })

  }

  useEffect(() => {
    dispatch(setTrajectoryStore({mileMarkerOffset}));
    // onMileMarkerOffsetChange()
  }, [mileMarkerOffset])

  // const onMileMarkerOffsetChange = () => {
  // }

  const copyToClipboard = str => {
    const el = document.createElement('textarea');
    el.value = str;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  };

  if (['owner', 'administrator', 'editor'].includes(store.role)) {
    return (
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%'}}>
        <DateRangeSelector
          style={{flexGrow: 1}}
          routeId={store.routeId}
          dateRangeId={store.dateRangeId}
          organizationName={store.organizationName}
          userEmail={store.userEmail}
          onChange={(dateRange) => {
            if (dateRange)
              dispatch(setTrajectoryStore({
                dateRangeId: dateRange.dateRangeId,
                startDate: dateRange.parsedStartDate,
                endDate: dateRange.parsedEndDate
              }))
          }}
        />
        <div style={{display: 'flex', paddingTop: '8px'}}>

          <Select
            style={{flex: '1 0 auto', width: '2em'}}
            value={store.routeId}
            placeholder="Select a route"
            disabled={routeSelectDisabled}
            showSearch
            onChange={(value, option) => {
              if (value === 'new') {
                // Reset the store to defaults
                routeIdChange(null);
                showRouteModal(false);
              } else routeIdChange(value)
            }}
            filterOption={(input, option) => {
              if (routes) {
                const fuse = new Fuse(routes, {keys: ['route_name'], threshold: 0.3});
                const result = fuse.search(input);
                return !!result.find(r => r.item.route_id === option.value);
              }
              return false;
            }}
            dropdownRender={menu => (
              <div>
                {menu}
                <Divider style={{margin: '4px 0'}}/>
                <Tooltip title="Shows only routes created by you">
                  <Space style={{padding: '12px'}}>
                    <Switch
                      defaultChecked={store.showOnlyMyRoutes}
                      onChange={checked => dispatch(setTrajectoryStore({showOnlyMyRoutes: checked}))}
                    />
                    <div>My routes only</div>
                  </Space>
                </Tooltip>
              </div>
            )}
          >
            <Option value="new" key={'new'}><b>New Route</b></Option>

            {routes &&
              routes.map(route => (
                (route.route_name || route.route_id === store.routeId) &&
                <Option value={route.route_id} key={route.route_id}>
                  <Tooltip
                    title={`Width: ${route.road_width} ft, Offset: ${route.mile_marker_offset} mi`}>
                    {route.route_name || '----Untitled route----'}
                  </Tooltip>
                </Option>
              ))
            }

            {(!!routes && !routes.find(r => r.route_id === store.routeId) && store.routeId) &&
              <Option value={store.routeId} key={store.routeId} disabled={true}>
                <Tooltip
                  title={`Width: ${store.roadWidth} ft, Offset: ${store.mileMarkerOffset} mi`}>
                  {(store.routeName || '----Untitled route----') + ' (External)'}
                </Tooltip>
              </Option>
            }
          </Select>
          <Space>
            <Tooltip title="Edit route (only internal routes)" placement="topRight">
              <Button disabled={editRouteDisabled} style={{marginLeft: '8px'}}
                      onClick={showRouteModal}>Edit</Button>
            </Tooltip>
            {/*<Button style={{marginTop: '8px'}} onClick={() => saveRoute(true)}>Duplicate</Button>*/}
          </Space>
        </div>
        <Space style={{marginTop: '8px', flexDirection: 'row-reverse'}} wrap={true}>
          <Tooltip title="Render and save heatmap and create link (only internal routes)"
                   placement="topRight">
            <Button type="primary" loading={saveLoading} disabled={editRouteDisabled} onClick={() => {
              if (!store.routeId) {
                showErrorToast("No route selected!");
                return;
              }
              if (!store.dateRangeId) {
                showErrorToast("No date range selected!")
                return;
              }
              dispatch(setTrajectoryStore({submitClicked: true}))
              createTrajectoryHeatmap();
            }}>Render and Save</Button>
          </Tooltip>
          <Button disabled={editRouteDisabled} onClick={() => {
            // TODO this is an absolutely awful way of doing it. The CFD should for sure use a route ID and do its own routing
            let equidistantRoute = findEquidistantPointsAlongRoute(store.route, store.distanceBetweenPoints);
            sessionStorage.setItem('route', JSON.stringify(equidistantRoute))
            sessionStorage.setItem('movementTable', JSON.stringify(store.movementTable))
            sessionStorage.setItem('organizationName', JSON.stringify(store.organizationName))
            // We don't want to pass the date range along if it's just the default (id: 1, Today -> Today-1)
            if (store.dateRangeId !== 1) window.open(`/cfd/${store.routeId}/${store.dateRangeId}`, '_blank')
            else window.open(`/cfd/${store.routeId}`, '_blank')
          }}>CFD</Button>
          <Tooltip title="Clear heatmap">
            <Button type="default" danger onClick={() => {
              let mapCenter = inputMapRef.current.getMapCenter();
              let mapZoom = inputMapRef.current.getMapZoom();
              localStorage.setItem('center', JSON.stringify(mapCenter))
              localStorage.setItem('zoom', mapZoom)
              window.location.href = window.location.protocol + '//' + window.location.host;
            }}>Clear</Button>
          </Tooltip>
          {/*<div>Heatmap ID: {store.heatmapId}</div>*/}
        </Space>
        <InputMap waypoints={waypoints}
                  id="bar-page-map"
                  ref={inputMapRef}
                  interactive={false}
                  className="input-item"
                  style={{
                    height: '500px',
                    flex: '1 1 auto',
                    width: '100%',
                    border: `1px solid ${useDarkMode ? 'rgb(67, 67, 67)' : 'rgb(217, 217, 217)'}`,
                    alignSelf: 'flex-start'
                  }}/>
        <Logos
          style={{
            marginTop: '10px',
            display: 'flex',
            position: 'relative',
            bottom: 0,
            marginRight: '8px',
            maxHeight: '150px'
          }}/>
        <Modal
          title="Edit Route"
          visible={routeModalVisible}
          footer={[
            <Button key='back' onClick={routeModalHandleCancel}>Cancel</Button>,
            <Button onClick={() => {
              saveRoute(true)
            }}>Duplicate</Button>,
            ...(store.routeId && store.routeCreatorEmail === store.userEmail) ?
              [<Button danger loading={deleteLoading}
                       onClick={routeModalHandleDelete}>Delete</Button>] : [],
            <Button type='primary' onClick={routeModalHandleOk} loading={confirmLoading}>Save</Button>
          ]}
          closable={false}
          width={1200}
        >
          {/*<div style={{display: 'flex'}}>*/}

          {/*</div>*/}
          <div style={{display: 'flex'}}>
                                <span style={{paddingTop: '2px', paddingRight: '8px'}}
                                      className="input-label">Name</span>
            <Input style={{width: '200px', marginRight: '8px'}}
                   value={routeName ?? store.routeName}
                   onChange={e => {
                     setRouteName(e.target.value)
                   }}
                   placeholder="Enter a name..."/>
            <Divider type="vertical" className='input-item' style={{paddingRight: '8px'}}/>
            <span style={{paddingTop: '2px'}} className="input-label ">Road Width</span>
            <div className="">
              <Slider
                style={{width: '200px', marginRight: '16px', marginLeft: '16px'}}
                value={typeof roadWidth === 'number' ? roadWidth : 10}
                step={10}
                min={10}
                max={300}
                onChange={val => {
                  setRoadWidth(val);
                  dispatch(setTrajectoryStore({roadWidth: val}));
                  dispatch(setTrajectoryStore({isChangingRoadWidth: true}));
                }}
                onAfterChange={roadWidth => dispatch(setTrajectoryStore({isChangingRoadWidth: false}))}
              />
            </div>
            <InputNumber
              min={10}
              max={300}
              value={Math.ceil(roadWidth / 10) * 10}
              step={10}
              style={{width: '70px'}}
              // formatter={value => `${value} feet`}
              // parser={value => value.replace(' feet', '')}
              onChange={val => {
                setRoadWidth(val);
                dispatch(setTrajectoryStore({roadWidth: val}))
              }}
            />
            <div style={{marginTop: '4px', marginLeft: '8px'}}>feet</div>
            <Divider type="vertical" className='input-item'
                     style={{paddingRight: '8px', marginLeft: '16px'}}/>
            <a style={{marginTop: '4px'}} onClick={() => {
              copyToClipboard(waypoints)
              toast.success('Copied Linestring to clipboard!')
            }}>Copy Linestring</a>

            {store.truckTable && (
              <>
                <Divider type="vertical" className='input-item'
                         style={{paddingRight: '8px', marginLeft: '16px'}}/>
                <Checkbox
                  checked={includeTrucks}
                  onChange={
                    event => {
                      let checked = event.target.checked;
                      setIncludeTrucks(checked);
                      dispatch(setTrajectoryStore({
                        includeTrucks: checked,
                      }));
                    }
                  }
                >Truck Data?</Checkbox>
              </>
            )
            }
          </div>
          <span className="input-label input-item">Mile Markers</span>
          <Space className="input-item">
            <Tooltip title="Y-axis offset">
              <Space>
                <span>Offset</span>
                <InputNumber
                  min={0}
                  value={mileMarkerOffset}
                  step="0.1"
                  style={{width: '70px'}}
                  onChange={val => {
                    setMileMarkerOffset(val)
                  }}/>
                <div>miles</div>
              </Space>
            </Tooltip>
            {['Default', 'JTRP', 'INDOT', 'Testing', 'TestingJCD','JTRP-New','INDOT-New','Caltrans-New','CTDOT-New','GDOT-New','MnDOT-New','MSDOT-New','NCDOT-New','ODOT-New','PennDOT-New','TxDOT-New','UDOT-New','WisDOT-New'].includes(store.organizationName) &&
              <a onClick={() => {
                if (suggestedMileMarker !== null) setMileMarkerOffset(parseFloat(suggestedMileMarker.toFixed(1)))
              }}>Use
                suggested: {typeof suggestedMileMarker === 'number' ? suggestedMileMarker.toFixed(1) : '----'}</a>
            }
            <Checkbox
              checked={flipMileMarkers}
              onChange={
                event => {
                  let checked = event.target.checked;
                  setFlipMileMarkers(checked);
                  dispatch(setTrajectoryStore({
                    flipMileMarkers: checked,
                  }));
                }
              }>Flip?</Checkbox>
            <Checkbox
              checked={subtractMileMarkers}
              disabled={subtractMileMarkersDisabled}
              style={{paddingRight: 0}}
              onChange={
                event => {
                  let checked = event.target.checked;
                  setSubtractMileMarkers(checked);
                  dispatch(setTrajectoryStore({
                    subtractMileMarkers: checked,
                  }));
                }
              }>Subtract?</Checkbox>
            <Popover title={'Flip and Subtract Guide'} content={(
              <Space direction={'vertical'}>
                <Space>All NB/EB routes:</Space>
                <Space>
                  <div style={{color: 'red'}}>❌ Flip</div>
                  <div style={{color: 'red'}}>❌ Subtract</div>
                </Space>
                <Space>SB/WB routes without offset:</Space>
                <Space>
                  <div style={{color: 'green'}}>✅ Flip</div>
                  <div style={{color: 'red'}}>❌ Subtract</div>
                </Space>
                <Space>SB/WB routes with offset &gt; length:</Space>
                <Space>
                  <div style={{color: 'red'}}>❌ Flip</div>
                  <div style={{color: 'green'}}>✅ Subtract</div>
                </Space>
              </Space>
            )}>
              <QuestionCircleOutlined style={{marginTop: '5px'}}/>
            </Popover>
            <Tooltip title="Reverse the route">
              <Button disabled={!store.route} onClick={() => {
                setFlipMileMarkers(!flipMileMarkers);
                let coordinates = wellknown.parse(store.waypoints);
                coordinates.coordinates.reverse();
                dispatch(setTrajectoryStore({
                  waypoints: wellknown.stringify(coordinates),
                  flipMileMarkers: !flipMileMarkers
                }))
              }}>Reverse</Button>
            </Tooltip>
            <Divider type="vertical"/>
            <span className="input-label input-item">Max Travel Time</span>
            <InputNumber
              value={maxTravelTime}
              step={0.1}
              min={0}
              onChange={value => {
                // dispatch(setTrajectoryStore({loadedMaxTravelTime: !!value}))
                setMaxTravelTime(value)
              }}
              formatter={value => (parseFloat(value)) ? parseFloat(value) : 'auto'}
              parser={value => (value === 'auto') ? 0 : parseFloat(value)}
              style={{width: '70px'}}/>
            <div>minutes</div>
          </Space>
          
          {['JTRP-New','INDOT-New','Caltrans-New','CTDOT-New','GDOT-New','MnDOT-New','MSDOT-New','NCDOT-New','ODOT-New','PennDOT-New','TxDOT-New','UDOT-New','WisDOT-New'].includes(store.organizationName) && (
            <Space>
              <Divider type="vertical" className='input-item' style={{ paddingRight: '8px' }} />

              <span style={{ paddingTop: '2px' }} className="input-label">
                Acceleration Threshold
              </span>

              <Slider
                style={{ width: '200px' }}
                value={typeof accelThreshold === 'number' ? accelThreshold : 0.01}
                step={0.01}
                min={0.1}
                max={0.5}
                onChange={val => {
                  setAccelThreshold(val);
                  dispatch(setTrajectoryStore({ accelThreshold: val }));
                }}
              />

              <InputNumber
                min={0.1}
                max={0.5}
                value={accelThreshold}
                step={0.01}
                style={{ width: '70px' }}
                onChange={val => {
                  setAccelThreshold(val);
                  dispatch(setTrajectoryStore({ accelThreshold: val }));
                }}
              />

              <span>g</span>
            </Space>
          )}

          <InputMap waypoints={waypoints}
                    id="modal-map"
                    ref={inputMapRef}
                    interactive={true}
                    className="input-item"
                    style={{
                      height: '50vh',
                      flex: '1 1 auto',
                      border: `1px solid ${useDarkMode ? 'rgb(67, 67, 67)' : 'rgb(217, 217, 217)'}`,
                      width: '100%',
                      alignSelf: 'flex-start'
                    }}/>

        </Modal>
      </div>
    )
  } else if (['viewer'].includes(store.role)) {
    return (
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%'}}>
        <InputMap waypoints={waypoints}
                  id="bar-page-map"
                  interactive={false}
                  ref={inputMapRef}
                  style={{
                    height: '500px',
                    flex: '1 1 auto',
                    width: '100%',
                    border: `1px solid ${useDarkMode ? 'rgb(67, 67, 67)' : 'rgb(217, 217, 217)'}`,
                    alignSelf: 'flex-start'
                  }}/>
        <Logos
          style={{
            marginTop: '10px',
            display: 'flex',
            position: 'relative',
            bottom: 0,
            marginRight: '8px',
            maxHeight: '150px'
          }}/>
      </div>
    )
  } else {
    return (
      <div style={{display: "flex", alignItems: 'center', justifyContent: 'center', flexDirection: 'column'}}>
        <Skeleton active={true}/>
        {/*<div>Loading...</div>*/}
        <br/>
        <div>If you see this for more than a couple seconds, third-party cookies may not be allowed for this
          site. <b>Check the far right of the URL bar for a cookie or eye icon, click "<i>Site not
            working?</i>",
            then "<i>Allow cookies</i>".</b> Otherwise, check <a
            href="https://www.123formbuilder.com/docs/how-to-enable-third-party-cookies-in-your-web-browser/">this
            help article</a>.
        </div>
      </div>
    )
  }
}

export default InputPage;
